/**
 * Runden-State-Machine (SCOPE_STAGE2 Punkt 1, docs/01):
 * PvE R1-3 (1 Attempt) -> PvP ab R4 (2 Attempts, Forced Progression) ->
 * wiederkehrende PvE-Runden ab R10 alle 5 (Fokus-Pick). HP-Verlust PRO LOSS.
 * Alles deterministisch aus masterSeed + Spieler-Entscheidungen.
 */
import { simulate } from '../sim/simulate'
import type { CombatResult, TeamSetup } from '../sim/types'
import { cloneConfig, type BalanceConfig } from '../config/balance'
import type { FocusId, RunState, RUN_STATE_VERSION } from './types'
import { RUN_STATE_VERSION as VERSION } from './types'
import { applyRoundIncome, outcomeMod, stageBase } from './economy'
import { buildPlayerTeam } from './roster'
import { confirmShop, enterShop, expireCrossBonuses } from './shop'
import { spawnFracture } from './fractures'
import { generateGhostSeat } from './ghostSeat'
import { drawU32 } from './rngStreams'

export type { RUN_STATE_VERSION }

/** PvE-Runde? R1-3 oder ab R10 alle 5 Runden (docs/01) */
export function isPveRound(round: number, cfg: BalanceConfig): boolean {
  if (round <= cfg.progression.pveEndRound) return true
  const start = cfg.progression.recurringPveStart
  return round >= start && (round - start) % cfg.progression.recurringPveInterval === 0
}

/** wiederkehrende PvE-Runde (Ort des Fokus-Picks)? */
export function isRecurringPve(round: number, cfg: BalanceConfig): boolean {
  return round > cfg.progression.pveEndRound && isPveRound(round, cfg)
}

export function newRun(masterSeed: number, config: BalanceConfig): RunState {
  const cfg = cloneConfig(config)
  const state: RunState = {
    version: VERSION,
    masterSeed: masterSeed >>> 0,
    round: 1,
    phase: 'shop',
    attempt: 1,
    lossesThisRound: 0,
    wonThisRound: false,
    hp: cfg.hp.start,
    gold: cfg.economy.startGold,
    nextUid: 1,
    champions: [],
    weapons: [],
    passives: [],
    fractures: [],
    lastFractureZone: -1,
    focus: null,
    bonusChamp: null,
    shop: {
      visit: 1, path: null, slots: [], paidRerollsUsed: 0, freeRerolls: 0,
      weaponRatio: cfg.shop.itemWeaponsDefault, repairsUsed: 0,
    },
    crossBonuses: [],
    recoveryTokens: 0,
    lowHpRerollRound: 0,
    craftedWeaponTiers: {},
    rngCounters: {},
    lastCombat: null,
    stats: { losses: 0, attempts: 0, cleanWins: 0 },
    config: cfg,
  }
  applyRoundIncome(state, { cleanWinLastRound: false, pveLoot: false })
  enterShop(state, 1)
  return state
}

/** Fokus-Pick (nur in wiederkehrenden PvE-Runden, Phase 'focusPick') */
export function pickFocus(state: RunState, focus: FocusId): boolean {
  if (state.phase !== 'focusPick') return false
  state.focus = focus
  if (focus === 'gold') {
    // Gold-Fokus: direkter Bonus statt RNG-Manipulation (docs/01) — v0: einmalig beim Pick (A-CC20)
    state.gold += state.config.focus.goldBonus
  }
  enterShop(state, 1)
  return true
}

export interface FightOutput {
  result: CombatResult | null // null = Auto-Loss ohne Kampf (leeres Board, A-CC31)
  playerTeamSize: number
  /** Setups des Kampfs — Stufe 3 persistiert sie im CombatLog (Replay via Re-Sim, A-CC38) */
  setupA: TeamSetup
  setupB: TeamSetup
}

/** S2 v1.2: kampfloser Loss bei leerem Board — normale Loss-Regeln (HP, Recovery, Fracture) */
function resolveAutoLoss(state: RunState): FightOutput {
  const cfg = state.config
  const sb = stageBase(state.round, cfg)
  const om = outcomeMod(10000, sb) // Gegner-Rest-Power 100%
  const hpLoss = sb + om
  state.hp = Math.max(0, state.hp - hpLoss)
  state.lossesThisRound++
  state.stats.losses++
  state.stats.attempts++
  let fractureSpawned = false
  if (state.lossesThisRound >= 2) {
    if (state.hp >= cfg.economy.lowHpThreshold) {
      state.recoveryTokens += cfg.economy.recoveryRerollTokens
    }
    fractureSpawned = spawnFracture(state)
  }
  state.lastCombat = {
    round: state.round,
    attempt: state.attempt,
    seed: 0,
    ghostSeed: 0,
    playerWon: false,
    autoLoss: true,
    winner: 'B',
    reason: 'elimination',
    enemyPowerBp: 10000,
    hpLoss,
    stageBase: sb,
    outcomeMod: om,
    fractureSpawned,
  }
  state.phase = 'result'
  return { result: null, playerTeamSize: 0, setupA: [], setupB: [] }
}

/**
 * Kampf ausfuehren (Shop -> Fight). Wendet HP-Verlust/Recovery/Fracture sofort an
 * und wechselt in die Result-Phase. Rueckgabe enthaelt das CombatResult fuers Playback.
 */
export function startFight(state: RunState): FightOutput | null {
  if (state.phase !== 'shop') return null

  // Shop abschliessen (Cross-Boni) — Teil der State-Machine, nicht des UI-Aufrufers (Review-Fix C9)
  confirmShop(state)

  const playerTeam = buildPlayerTeam(state)
  if (playerTeam.length === 0) {
    // S2 v1.2 Bodennetz (A-CC31): leeres Board = kampfloser Auto-Loss.
    // Deterministisch ohne Sim: Rest-Power Gegner = 100% -> Damage = StageBase + OutcomeMod(100%).
    // Loescht die gesamte Soft-Lock-Klasse (Item-Pfad ohne Champs, Sell-to-Zero, Broke-Lock).
    return resolveAutoLoss(state)
  }

  const cfg = state.config
  const pve = isPveRound(state.round, cfg)
  // A-CC37: der Ghost ist PRO RUNDE fix — Attempt 2 kaempft gegen DASSELBE Team
  // (Stufe-3-Seat-Modell vorweggenommen; Konter-Kaeufe im 2. Shop ergeben sonst keinen Sinn).
  // Fallback fuer alte Saves ohne Feld: frisch ziehen.
  if (state.attempt === 1 || state.roundGhostSeed == null) {
    state.roundGhostSeed = drawU32(state.masterSeed, state.rngCounters, 'ghost')
  }
  const ghostSeed = state.roundGhostSeed
  const ghost = generateGhostSeat(ghostSeed, state.round, cfg, { pve })
  const combatSeed = drawU32(state.masterSeed, state.rngCounters, 'combat')
  const result = simulate(combatSeed, playerTeam, ghost, cfg)

  const playerWon = result.winner === 'A' // draw zaehlt als Loss (A-CC19)
  state.stats.attempts++
  let hpLoss = 0
  const sb = stageBase(state.round, cfg)
  let om = 0
  let fractureSpawned = false

  if (playerWon) {
    if (state.attempt === 1) {
      state.wonThisRound = true // Clean Win -> Win-Bonus naechste Runde
      state.stats.cleanWins++
    }
  } else {
    // HP-Verlust PRO LOSS (docs/01): Damage = StageBase + OutcomeMod
    om = outcomeMod(result.scoreB.powerBp, sb)
    hpLoss = sb + om
    state.hp = Math.max(0, state.hp - hpLoss)
    state.lossesThisRound++
    state.stats.losses++
    if (state.lossesThisRound >= 2) {
      // Recovery (deaktiviert im Low HP Mode) + Fracture (docs/01)
      if (state.hp >= cfg.economy.lowHpThreshold) {
        state.recoveryTokens += cfg.economy.recoveryRerollTokens
      }
      fractureSpawned = spawnFracture(state)
    }
  }

  state.lastCombat = {
    round: state.round,
    attempt: state.attempt,
    seed: combatSeed,
    ghostSeed,
    playerWon,
    autoLoss: false,
    winner: result.winner,
    reason: result.reason,
    enemyPowerBp: result.scoreB.powerBp,
    hpLoss,
    stageBase: sb,
    outcomeMod: om,
    fractureSpawned,
  }
  state.phase = 'result'
  return { result, playerTeamSize: playerTeam.length, setupA: playerTeam, setupB: ghost }
}

/**
 * Rundenwechsel — GEMEINSAME Logik fuer den echten Run (continueAfterResult) und
 * den Ghost-Run (ghostSeat.ts, A-CC35). Nicht duplizieren: Drift zwischen beiden
 * Pfaden ist exakt die BATTLE-19-Bug-Klasse.
 */
export function advanceRound(state: RunState, opts: { cleanWinLastRound: boolean }): void {
  const cfg = state.config
  state.round++
  state.attempt = 1
  state.lossesThisRound = 0
  state.wonThisRound = false
  expireCrossBonuses(state)
  // Fokus gilt bis zur NAECHSTEN PvE-Runde, dann Reset + Neuwahl (docs/01)
  if (isRecurringPve(state.round, cfg)) state.focus = null
  const pveLoot = isRecurringPve(state.round, cfg)
  applyRoundIncome(state, { cleanWinLastRound: opts.cleanWinLastRound, pveLoot })
  if (isRecurringPve(state.round, cfg)) {
    state.phase = 'focusPick'
  } else {
    enterShop(state, 1)
  }
}

/**
 * Result bestaetigen ("Weiter"): entweder 2. Shop (PvP, Attempt 1 verloren)
 * oder Rundenende (Forced Progression — Stage advanciert IMMER, docs/01).
 */
export function continueAfterResult(state: RunState): void {
  if (state.phase !== 'result' || !state.lastCombat) return
  const cfg = state.config
  if (state.hp <= 0) {
    state.phase = 'ended'
    return
  }
  const pve = isPveRound(state.round, cfg)
  const lost = !state.lastCombat.playerWon
  if (lost && !pve && state.attempt === 1) {
    // Attempt 2: 2. Shop, Reroll-Sequenz startet frisch (docs/02)
    state.attempt = 2
    enterShop(state, 2)
    return
  }
  advanceRound(state, { cleanWinLastRound: state.wonThisRound })
}
